import {
  useMutation as useApolloMutation,
  useQuery as useApolloQuery,
} from '@apollo/client';
import { useQuery } from '@tanstack/react-query';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  NewSemenInput,
  ODSemenItem,
  odSemenOptions,
  odSimilarSemenOptions,
  PulledSemenItem,
  pulledSemenOptions,
  PullODSemenRequest,
  PullSemenRequest,
  RejectDiscardSemenRequest,
  ReturnSemenRequest,
  ScheduledDonorItem,
  scheduledDonorsOptions,
  ScheduledSemenItem,
  scheduledSemenOptions,
  scheduledSemenV2Options,
  sslQcOptions,
  unfinishedScheduledDonorsOptions,
  UpdateDSSuggestedRequest,
  UpdateSemenInput,
  useCreateNewSemenMutation,
  usePullODSemenMutation,
  usePullSemenMutation,
  useRejectDiscardSemenMutation,
  useReturnSemenMutation,
  useSaveSslQcMutation,
  useUpdateDSSuggestedMutation,
  useUpdateSemenMutation,
} from '@/services/semen-pull';
import {
  CREATE_NEW_SEMEN,
  GET_OD_SEMEN,
  GET_OD_SIMILAR_SEMEN,
  GET_PULLED_SEMEN,
  GET_SCHEDULED_DONORS,
  GET_UNFINISHED_SCHEDULED_DONORS,
  PULL_OD_SEMEN,
  PULL_SEMEN,
  REJECT_DISCARD_SEMEN,
  RETURN_SEMEN,
  SCHEDULED_SEMEN,
  UPDATE_DS_SUGGESTED,
  UPDATE_SEMEN,
} from '@/types.gql';

const FLAG = '29181_semen_pull_graphql_to_rest';

export function useScheduledSemenQuery(params: {
  date: string | null;
  fertilizationSiteId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enableSireNote = useFeatureFlag('Add_Sire_Note');
  const enabled = !!params.date && !!params.fertilizationSiteId;

  const restQuery = useQuery({
    ...(enableSireNote
      ? scheduledSemenV2Options({
          date: params.date!,
          fertilizationSiteId: params.fertilizationSiteId!,
        })
      : scheduledSemenOptions({
          date: params.date!,
          fertilizationSiteId: params.fertilizationSiteId!,
        })),
    enabled: isRest && enabled,
  });

  const gqlQuery = useApolloQuery(SCHEDULED_SEMEN, {
    skip: isRest || !enabled,
    variables: {
      date: params.date!,
      fertilizationSiteId: params.fertilizationSiteId!,
    },
    fetchPolicy: 'network-only',
  });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.scheduledSemen as ScheduledSemenItem[] | undefined,
    isLoading: gqlQuery.loading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function usePulledSemenQuery(params: {
  date: string | null;
  fertilizationSiteId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.date && !!params.fertilizationSiteId;

  const restQuery = useQuery({
    ...pulledSemenOptions({
      date: params.date!,
      fertilizationSiteId: params.fertilizationSiteId!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_PULLED_SEMEN, {
    skip: isRest || !enabled,
    variables: {
      date: params.date!,
      fertilizationSiteId: params.fertilizationSiteId!,
    },
    fetchPolicy: 'network-only',
  });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
      refetch: () => {
        void restQuery.refetch();
      },
    };
  }
  return {
    data: gqlQuery.data?.getPulledSemen as PulledSemenItem[] | undefined,
    isLoading: gqlQuery.loading,
    refetch: () => {
      void gqlQuery.refetch();
    },
  };
}

export function useUnfinishedScheduledDonorsQuery(params: {
  date: string | null;
  siteId: string | null;
}) {
  const isRest = useFeatureFlag(FLAG);
  const enabled = !!params.date && !!params.siteId;

  const restQuery = useQuery({
    ...unfinishedScheduledDonorsOptions({
      donorScheduleDate: params.date!,
      fertSiteId: params.siteId!,
    }),
    enabled: isRest && enabled,
  });

  const gqlQuery = useApolloQuery(GET_UNFINISHED_SCHEDULED_DONORS, {
    skip: isRest || !enabled,
    variables: {
      donorScheduleDate: params.date!,
      fertSiteId: params.siteId!,
    },
    fetchPolicy: 'network-only',
  });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
    };
  }
  return {
    data: gqlQuery.data?.getUnfinishedScheduledDonors as
      | ScheduledDonorItem[]
      | undefined,
    isLoading: gqlQuery.loading,
  };
}

export function useScheduledDonorsQuery(params: {
  date: string | null;
  siteId: string | null;
  sireId?: string | null;
  semenId?: string | null;
  accountId?: string | null;
  semenType?: string | null;
  dsSemenLocId?: string | null;
  enabled?: boolean;
}) {
  const isRest = useFeatureFlag(FLAG);
  const canFetch =
    (params.enabled ?? true) &&
    !!params.date &&
    !!params.siteId &&
    !!params.sireId;

  const restQuery = useQuery({
    ...scheduledDonorsOptions({
      fertilizationSiteId: params.siteId!,
      donorScheduleDate: params.date!,
      sireId: params.sireId!,
      semenId: params.semenId,
      accountId: params.accountId,
      semenType: params.semenType,
      dsSemenLocId: params.dsSemenLocId,
    }),
    enabled: isRest && canFetch,
  });

  const gqlQuery = useApolloQuery(GET_SCHEDULED_DONORS, {
    skip: isRest || !canFetch,
    variables: {
      DonorScheduleDate: params.date!,
      fertilizationSiteId: params.siteId!,
      SireId: params.sireId!,
      SemenId: params.semenId,
      SemenType: params.semenType,
      AccountId: params.accountId,
      DSSemenLocId: params.dsSemenLocId,
    },
    fetchPolicy: 'network-only',
  });

  if (isRest) {
    return {
      data: restQuery.data,
      isLoading: restQuery.isFetching,
    };
  }
  return {
    data: gqlQuery.data?.getScheduledDonors as ScheduledDonorItem[] | undefined,
    isLoading: gqlQuery.loading,
  };
}

export function useODSemenQuery(
  params: {
    fertSiteId: string;
    semenName?: string;
    accountName?: string;
    caneCode?: string;
    colCode?: string;
  } | null,
  enabled: boolean,
) {
  const isRest = useFeatureFlag(FLAG);
  const canFetch = enabled && !!params;

  const restQuery = useQuery({
    ...odSemenOptions(params ?? { fertSiteId: '' }),
    enabled: isRest && canFetch,
    staleTime: 0,
  });

  const gqlQuery = useApolloQuery(GET_OD_SEMEN, {
    skip: isRest || !canFetch,
    variables: params ?? { fertSiteId: '' },
    fetchPolicy: 'network-only',
  });

  if (isRest) {
    return {
      data: restQuery.data as ODSemenItem[] | undefined,
      isFetching: restQuery.isFetching,
    };
  }
  return {
    data: gqlQuery.data?.odSemen as ODSemenItem[] | undefined,
    isFetching: gqlQuery.loading,
  };
}

export function useODSimilarSemenQuery(
  params: {
    fertSiteId: string;
    semenName?: string;
    accountName?: string;
    caneCode?: string;
    colCode?: string;
  } | null,
  enabled: boolean,
) {
  const isRest = useFeatureFlag(FLAG);
  const canFetch = enabled && !!params;

  const restQuery = useQuery({
    ...odSimilarSemenOptions(params ?? { fertSiteId: '' }),
    enabled: isRest && canFetch,
    staleTime: 0,
  });

  const gqlQuery = useApolloQuery(GET_OD_SIMILAR_SEMEN, {
    skip: isRest || !canFetch,
    variables: params ?? { fertSiteId: '' },
    fetchPolicy: 'network-only',
  });

  if (isRest) {
    return {
      data: restQuery.data as ODSemenItem[] | undefined,
      isFetching: restQuery.isFetching,
    };
  }
  return {
    data: gqlQuery.data?.odSimilarSemen as ODSemenItem[] | undefined,
    isFetching: gqlQuery.loading,
  };
}

export function usePullSemen() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = usePullSemenMutation();
  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(PULL_SEMEN);

  const mutateAsync = async (request: PullSemenRequest) => {
    if (isRest) {
      return restMutation.mutateAsync(request);
    }
    return gqlMutate({
      variables: {
        donorScheduleDate: request.donorScheduleDate,
        fertSiteId: request.fertSiteId,
        semenId: request.semenId,
        moveNum: request.moveNum,
        semenType: request.semenType,
        accountId: request.accountId,
        semenOwnerId: request.semenOwnerId,
        dsSemenLocId: request.dsSemenLocId,
      },
    });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useReturnSemen() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useReturnSemenMutation();
  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(RETURN_SEMEN);

  const mutateAsync = async (request: ReturnSemenRequest) => {
    if (isRest) {
      return restMutation.mutateAsync(request);
    }
    return gqlMutate({
      variables: {
        SemenId: request.semenId,
        ReturnNum: request.returnNum,
        SemenType: request.semenType,
      },
    });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useRejectDiscardSemen() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useRejectDiscardSemenMutation();
  const [gqlMutate, { loading: gqlLoading }] =
    useApolloMutation(REJECT_DISCARD_SEMEN);

  const mutateAsync = async (request: RejectDiscardSemenRequest) => {
    if (isRest) {
      return restMutation.mutateAsync(request);
    }
    return gqlMutate({
      variables: {
        SemenId: request.semenId,
        RejectNum: request.rejectNum,
        SemenType: request.semenType,
        Description: request.description,
      },
    });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function usePullODSemen() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = usePullODSemenMutation();
  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(PULL_OD_SEMEN);

  const mutateAsync = async (request: PullODSemenRequest) => {
    if (isRest) {
      return restMutation.mutateAsync(request);
    }
    return gqlMutate({
      variables: {
        semenId: request.semenId,
        semenType: request.semenType,
        moveNum: request.moveNum,
      },
    });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useCreateSemen() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useCreateNewSemenMutation();
  const [gqlMutate, { loading: gqlLoading }] =
    useApolloMutation(CREATE_NEW_SEMEN);

  const mutateAsync = async (newSemen: NewSemenInput) => {
    if (isRest) {
      return restMutation.mutateAsync(newSemen);
    }
    const { secondaryOwner: _secondaryOwner, ...gqlInput } = newSemen;
    return gqlMutate({ variables: { newSemen: gqlInput } });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateSemen() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateSemenMutation();
  const [gqlMutate, { loading: gqlLoading }] = useApolloMutation(UPDATE_SEMEN);

  const mutateAsync = async (updateData: UpdateSemenInput) => {
    if (isRest) {
      return restMutation.mutateAsync(updateData);
    }
    const { secondaryOwner: _secondaryOwner, ...gqlInput } = updateData;
    return gqlMutate({ variables: { newSemen: gqlInput } });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useUpdateDSSuggested() {
  const isRest = useFeatureFlag(FLAG);
  const restMutation = useUpdateDSSuggestedMutation();
  const [gqlMutate, { loading: gqlLoading }] =
    useApolloMutation(UPDATE_DS_SUGGESTED);

  const mutateAsync = async (request: UpdateDSSuggestedRequest) => {
    if (isRest) {
      return restMutation.mutateAsync(request);
    }
    return gqlMutate({
      variables: {
        field: request.field,
        value: request.value,
        donorScheduleDate: request.donorScheduleDate,
        fertSiteId: request.fertSiteId,
        semenId: request.semenId,
        semenType: request.semenType,
        sireId: request.sireId,
        accountId: request.accountId,
        dsSemenLocId: request.dsSemenLocId,
      },
    });
  };

  return {
    mutateAsync,
    isPending: isRest ? restMutation.isPending : gqlLoading,
  };
}

export function useSslQcQuery(params: {
  date: string | null;
  siteId: string | null;
}) {
  const enabled = !!params.date && !!params.siteId;

  return useQuery({
    ...sslQcOptions({
      date: params.date!,
      siteId: params.siteId!,
    }),
    enabled,
  });
}

export { useSaveSslQcMutation };
